import { Box, Divider, Paper, Popover, Typography } from '@mui/material'
import { Button } from 'Common/Supplyvue-ui'
import { OutputPopperProps } from './SchedulerView.types'

export const TimeHeader = ({ time }: { time: string }) => (
  <Box
    style={{
      height: '200px',
      width: '50px',
      transform: 'rotate(90deg)',
      whiteSpace: 'nowrap',
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    {time}
  </Box>
)

export const OutputPopper = ({
  rowName,
  tableType = 'kitchen',
  start_time,
  end_time,
  base_material_description,
  slot_types,
  crossReference,
  changeoverDetails,
  open,
  anchorPosition,
  handleClose,
  anchorEl,
  id,
}: OutputPopperProps) => {
  const processLabel = slot_types?.[0]?.replace(/_/g, ' ') ?? 'Production'
  const duration =
    start_time != null && end_time != null ? `${end_time - start_time}h` : null
  const crossRefLabel =
    tableType.toLowerCase() === 'kitchen' ? 'Lines' : 'Kitchen'
  const uniqueCrossRef = crossReference ? [...new Set(crossReference)] : []
  // What the change actually is, e.g. `BTL01 - 1L Saffire to 2L Saffire`.
  // A non-format changeover has no from/to pair, so it falls back to the
  // engine's own reason text; an event with neither says nothing rather
  // than rendering an empty row.
  const changeoverLines = (changeoverDetails ?? [])
    .map((d) => {
      const what =
        d.from || d.to
          ? `${d.from ?? '(none)'} to ${d.to ?? '(none)'}`
          : d.raw
      if (!what) return null
      const cost =
        d.cost != null && d.cost > 0 ? ` - cost ${d.cost}` : ''
      const held = d.heldResource ? ` - holds ${d.heldResource}` : ''
      return `${d.resource} - ${what}${cost}${held}`
    })
    .filter((line): line is string => Boolean(line))

  return (
    <Popover
      id={id}
      open={open}
      anchorReference="anchorPosition"
      anchorEl={anchorEl}
      anchorPosition={anchorPosition}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '10px',
            width: 320,
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
        },
      }}
    >
      {/* Header — resource name + process type */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor: (theme) => theme.palette.primary.main,
          borderBottom: (theme) => `3px solid ${theme.palette.secondary.main}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: '0.85rem',
            letterSpacing: '0.02em',
          }}
        >
          {rowName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.75)',
            fontWeight: 500,
            fontSize: '0.65rem',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {processLabel}
        </Typography>
      </Box>

      {/* Body */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {/* What the changeover is — line, and the format it moves between. */}
        {changeoverLines.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Change
            </Typography>
            {changeoverLines.map((line) => (
              <Typography
                key={line}
                variant="body2"
                sx={{ fontWeight: 500, mt: 0.25 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}

        {/* Material */}
        {base_material_description && base_material_description.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Material
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
              {[...new Set(base_material_description)].join(', ')}
            </Typography>
          </Box>
        )}

        {/* Cross-reference (Lines for kitchen view, Kitchen for line view) */}
        {uniqueCrossRef.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {crossRefLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
              {uniqueCrossRef.join(', ')}
            </Typography>
          </Box>
        )}

        {/* Time */}
        {start_time != null && end_time != null && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Time
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
                mt: 0.25,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {start_time}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                to
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {end_time}
              </Typography>
              {duration && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', ml: 0.5 }}
                >
                  ({duration})
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Divider />
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={(event) => {
            event.stopPropagation()
            handleClose()
          }}
        >
          Close
        </Button>
      </Box>
    </Popover>
  )
}
